import { Box, HStack, Skeleton, VStack } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { formatChartDate } from "@hydrologiq/shared/utils/reporting/dateHelpers"
import { calculateCumulativeData } from "@hydrologiq/shared/utils/reporting/chartHelpers"
import { useMemo } from "react"

interface DailySummary {
  date: string
  quantity: number
  quantity_consumed: number
}

interface CustomerPerformanceChartProps {
  dailySummary: DailySummary[]
  loading?: boolean
  chartView: "daily" | "cumulative"
  onChartViewChange: (view: "daily" | "cumulative") => void
  startDate?: string
  endDate?: string
}

export function CustomerPerformanceChartSkeleton() {
  return <Skeleton height="400px" borderRadius="md" />
}

// Helper function to fill in missing dates in the range
function fillMissingDates(dailySummary: DailySummary[], startDateStr?: string, endDateStr?: string): any[] {
  // If no date range provided and no data, return empty
  if (dailySummary.length === 0 && !startDateStr && !endDateStr) return []

  const sortedSummary = [...dailySummary].sort((a, b) => a.date.localeCompare(b.date))

  // Use provided date range if available, otherwise use data range
  let startDate: Date
  let endDate: Date

  if (startDateStr && endDateStr) {
    startDate = new Date(startDateStr)
    endDate = new Date(endDateStr)
  } else if (sortedSummary.length > 0) {
    startDate = new Date(sortedSummary[0].date)
    endDate = new Date(sortedSummary[sortedSummary.length - 1].date)
  } else {
    return []
  }

  const filledData: any[] = []
  const dataMap = new Map(
    sortedSummary.map((item) => [
      item.date,
      {
        date: item.date,
        total_quantity: item.quantity,
        completed_quantity: item.quantity_consumed,
        future_quantity: item.quantity - item.quantity_consumed,
      },
    ])
  )

  const currentDate = new Date(startDate)
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split("T")[0]
    if (dataMap.has(dateStr)) {
      filledData.push(dataMap.get(dateStr)!)
    } else {
      filledData.push({
        date: dateStr,
        total_quantity: 0,
        completed_quantity: 0,
        future_quantity: 0,
      })
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return filledData
}

export default function CustomerPerformanceChart({
  dailySummary,
  loading = false,
  chartView,
  onChartViewChange,
  startDate,
  endDate,
}: CustomerPerformanceChartProps) {
  if (loading) {
    return <CustomerPerformanceChartSkeleton />
  }

  const filledDailySummary = useMemo(
    () => fillMissingDates(dailySummary, startDate, endDate),
    [dailySummary, startDate, endDate]
  )

  const dailyData = useMemo(() => {
    return filledDailySummary.map((day) => ({
      date: day.date,
      consumed: Math.round(day.completed_quantity * 10) / 10,
      planned: Math.round(day.future_quantity * 10) / 10,
      total: Math.round(day.total_quantity * 10) / 10,
    }))
  }, [filledDailySummary])

  const cumulativeData = useMemo(() => {
    return calculateCumulativeData(filledDailySummary).map((point) => ({
      date: point.date,
      cumulative_completed: Math.round(point.cumulative_completed * 10) / 10,
      cumulative_total: Math.round(point.cumulative_total * 10) / 10,
    }))
  }, [filledDailySummary])

  const maxValue = useMemo(() => {
    if (chartView === "daily") {
      return Math.ceil(Math.max(...dailyData.map((d) => d.total)))
    } else {
      return Math.ceil(Math.max(...cumulativeData.map((d) => d.cumulative_total)))
    }
  }, [chartView, dailyData, cumulativeData])

  // Chakra chart configuration for daily view
  const dailyChart = useChart({
    data: dailyData,
    series: [
      { name: "consumed", color: "green.500", label: "Consumed (kg)" },
      { name: "planned", color: "blue.300", label: "Planned (kg)" },
    ],
  })

  // Chakra chart configuration for cumulative view
  const cumulativeChart = useChart({
    data: cumulativeData,
    series: [
      { name: "cumulative_completed", color: "green.500", label: "Cumulative Consumed (kg)" },
      { name: "cumulative_total", color: "blue.500", label: "Cumulative Total (kg)" },
    ],
  })

  const dateFormatter = (date: string) => formatChartDate(date)

  if (dailySummary.length === 0) {
    return (
      <Box p={8} textAlign="center" borderWidth="1px" borderColor="border" borderRadius="md">
        <VStack gap={2}>
          <Box fontSize="md" fontWeight="semibold" color="fg.muted">
            No data available
          </Box>
          <Box fontSize="sm" color="fg.subtle">
            No fulfillments found for the selected period
          </Box>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* Chart Controls */}
      <HStack justify="space-between">
        <Box fontSize="lg" fontWeight="semibold">
          Hydrogen Consumption
        </Box>
        <SegmentedControl
          items={[
            { value: "daily", label: "Daily" },
            { value: "cumulative", label: "Cumulative" },
          ]}
          value={chartView}
          onValueChange={(e) => onChartViewChange(e.value as "daily" | "cumulative")}
        />
      </HStack>

      {/* Chart */}
      <Box borderWidth="1px" borderColor="border" borderRadius="md" p={4}>
        {chartView === "daily" ? (
          <Chart.Root height="400px" chart={dailyChart}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyChart.data}>
                <CartesianGrid stroke={dailyChart.color("border.muted")} vertical={false} />
                <XAxis
                  axisLine={false}
                  tickLine={false}
                  dataKey={dailyChart.key("date")}
                  tickFormatter={dateFormatter}
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  domain={[0, maxValue]}
                  tickFormatter={(value) => `${value} kg`}
                  style={{ fontSize: "12px" }}
                />
                <Tooltip
                  cursor={{ fill: dailyChart.color("bg.muted") }}
                  labelFormatter={dateFormatter}
                  animationDuration={0}
                  content={<Chart.Tooltip />}
                />
                {dailyChart.series.map((item) => (
                  <Bar
                    key={item.name}
                    isAnimationActive={false}
                    dataKey={dailyChart.key(item.name)}
                    fill={dailyChart.color(item.color)}
                    stackId="a"
                    radius={item.name === "planned" ? [4, 4, 0, 0] : undefined}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Chart.Root>
        ) : (
          <Chart.Root height="400px" chart={cumulativeChart}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cumulativeChart.data}>
                <CartesianGrid stroke={cumulativeChart.color("border.muted")} vertical={false} />
                <XAxis
                  axisLine={false}
                  tickLine={false}
                  dataKey={cumulativeChart.key("date")}
                  tickFormatter={dateFormatter}
                  style={{ fontSize: "12px" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  domain={[0, maxValue]}
                  tickFormatter={(value) => `${value} kg`}
                  style={{ fontSize: "12px" }}
                />
                <Tooltip
                  cursor={{ fill: cumulativeChart.color("bg.muted") }}
                  labelFormatter={dateFormatter}
                  animationDuration={0}
                  content={<Chart.Tooltip />}
                />
                {cumulativeChart.series.map((item) => (
                  <Line
                    key={item.name}
                    type="monotone"
                    isAnimationActive={false}
                    dataKey={cumulativeChart.key(item.name)}
                    stroke={cumulativeChart.color(item.color)}
                    strokeWidth={3}
                    dot={{ fill: cumulativeChart.color(item.color), r: 4 }}
                    activeDot={{ r: 6 }}
                    strokeDasharray={item.name === "cumulative_total" ? "5 5" : undefined}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Chart.Root>
        )}
      </Box>
    </VStack>
  )
}

